import { useEffect, useState } from 'react'
import { Props } from './Layout'
import { useLoginUser } from '../../states/LoginUser'
import {
  CollectibleItemCategory,
  IconType,
  useCurrentUserCollectibleItemsQuery,
  useListOwnedNfTsQuery,
  useUpdateUserIconMutation,
} from './query.generated'
import { ApolloError } from '@apollo/client'
import { ExternalUrlService } from '@/src/services/ExternalUrlService'
import { S3URLService } from '@/src/services/S3URLService'

export type Params = {
  onClickCancel: () => void
  onSaveIcon: () => void
}

export const useProps = (params: Params): Props => {
  const loginUser = useLoginUser().loginUser
  const name = loginUser?.name
  const userIconType = loginUser?.iconType ?? ''
  const userIconSubCategory = loginUser?.iconSubCategory ?? ''
  const userFrameSubCategory = loginUser?.frameSubCategory ?? ''
  const titleSubCategory = loginUser?.titleSubCategory ?? ''

  const [state, setState] = useState<Props['state']>('Default')
  const [selectTab, setSelectTab] = useState<Props['selectTab']>('Icon')
  const [inWorldIconList, setInWorldIconList] = useState<Props['inWorldIconList']>([])
  const [myPfpIconList, setMyPfpIconList] = useState<Props['myPfpIconList']>([])
  const [frameList, setFrameList] = useState<Props['frameList']>([])
  const [selectIcon, setSelectIcon] = useState<Props['selectIcon']>(null)
  const [savedIcon, setSavedIcon] = useState<Props['savedIcon']>(null)
  const [selectFrame, setSelectFrame] = useState<Props['selectFrame']>(null)
  const [savedFrame, setSavedFrame] = useState<Props['savedFrame']>(null)

  const { data: inWorldData } = useCurrentUserCollectibleItemsQuery({
    variables: { where: { category: { in: [CollectibleItemCategory.Icon, CollectibleItemCategory.Frame] } } },
  })
  const { data: nftData } = useListOwnedNfTsQuery()
  const [updateUser] = useUpdateUserIconMutation()

  useEffect(() => {
    setMyPfpIconList(
      (nftData?.listOwnedNFTs ?? []).map((e) => ({
        type: IconType.Nft,
        id: e.tokenId,
        name: e.name,
        imageUrl: S3URLService.getIconURL(IconType.Nft, e.tokenId),
        category: CollectibleItemCategory.Icon,
        subCategory: e.tokenId,
      })),
    )
  }, [nftData])

  useEffect(() => {
    const iconList = [
      {
        type: IconType.InWorld,
        id: 'Default',
        name: 'Default',
        imageUrl: S3URLService.getIconURL(IconType.InWorld, 'DEFAULT'),
        category: CollectibleItemCategory.Icon,
        subCategory: 'DEFAULT',
      },
      ...(inWorldData?.currentUserCollectibleItems
        .filter((item) => item.category === CollectibleItemCategory.Icon)
        .map((item) => ({
          type: IconType.InWorld,
          id: item.id,
          name: item.name || '',
          imageUrl: S3URLService.getIconURL(IconType.InWorld, item.subCategory),
          category: item.category,
          subCategory: item.subCategory,
        })) ?? []),
    ]
    setInWorldIconList(iconList)

    const frameList = [
      {
        id: 'Default',
        name: 'Default',
        imageUrl: S3URLService.getFrameURL('DEFAULT'),
        category: CollectibleItemCategory.Frame,
        subCategory: 'DEFAULT',
      },
      ...(inWorldData?.currentUserCollectibleItems
        .filter((item) => item.category === CollectibleItemCategory.Frame)
        .map((item) => ({
          id: item.id,
          name: item.name || '',
          imageUrl: S3URLService.getFrameURL(item.subCategory),
          category: CollectibleItemCategory.Frame,
          subCategory: item.subCategory,
        })) ?? []),
    ]
    setFrameList(frameList)
  }, [inWorldData])

  useEffect(() => {
    const savedIcon =
      userIconType === IconType.InWorld
        ? inWorldIconList.find((item) => item.subCategory === userIconSubCategory) ?? null
        : myPfpIconList.find((item) => item.subCategory === userIconSubCategory) ?? null
    setSelectIcon(savedIcon)
    setSavedIcon(savedIcon)

    const savedFrame = frameList.find((item) => item.subCategory === userFrameSubCategory) ?? null
    setSelectFrame(savedFrame)
    setSavedFrame(savedFrame)
  }, [inWorldIconList, myPfpIconList, frameList, userIconType, userIconSubCategory, userFrameSubCategory])

  return {
    state,
    selectTab,
    onClickTab: (tab) => setSelectTab(tab),
    onClickCancel: params.onClickCancel,
    onClickSave: async () => {
      setState('Loading')
      try {
        const res = await updateUser({
          variables: {
            input: {
              name,
              iconType: selectIcon?.type,
              iconSubCategory: selectIcon?.subCategory,
              frameSubCategory: selectFrame?.subCategory,
              titleSubCategory,
            },
          },
        })
        if (res.data) {
          setState('Default')
          params.onSaveIcon()
        } else {
          alert('Error')
          console.error(res.errors)
        }
      } catch (e) {
        const error = e as ApolloError
        setState('Default')
        alert('Error: ' + error.message)
        console.error(e)
      }
    },

    inWorldIconList,
    myPfpIconList,
    selectIcon,
    savedIcon,
    onClickIcon: (selectIcon) => setSelectIcon(selectIcon),
    onClickPfpOfficialSite: () => window.open(ExternalUrlService.getPfpOfficialSite()),

    frameList,
    selectFrame,
    savedFrame,
    onClickFrame: (selectFrame) => setSelectFrame(selectFrame),
  }
}
